/**
 * Where a reply stops and the quoted history begins.
 *
 * Shared by ingest, which strips the quotation to build the inbox preview, and
 * by the reader, which has to answer a different question about the same text:
 * *is* there quoted history under this message, and where does it start.
 *
 * One module rather than one copy on each side, because two copies of this rule
 * is one bug waiting for a mail client. The two sides disagreeing does not fail
 * loudly — it shows a message that stops mid-sentence, or offers a "show the
 * rest" button on a message that has no rest.
 *
 * That is not hypothetical. A reader built on this idea originally used the
 * stored snippet as the message body, on the assumption that it was "the text
 * minus the quotation". It is not: it is that, *then* whitespace-collapsed,
 * *then* cut to 180 characters. Every message longer than a short paragraph
 * rendered as a truncated single line behind a button that claimed to be about
 * quoting.
 *
 * ── The markers ──────────────────────────────────────────────────────
 *
 * Every mail client marks the boundary differently and none of them agree, so
 * this is the list of shapes that actually turn up: the `>` convention, Gmail's
 * and Outlook's attribution lines in English and Spanish, and the `--`
 * signature separator.
 *
 * Deliberately not exhaustive. Missing one costs a longer preview; matching too
 * eagerly hides a reply's first paragraph, which is much worse — so when in
 * doubt this list stays short.
 */
/**
 * "On Mon, 17 Aug 2026 at 11:55, Someone <x@y.com> wrote:"
 *
 * Gmail wraps this line at the address, so in a real message it arrives as two:
 * one ending in `<` and one beginning with the rest of the address. The pattern
 * is therefore also tried against each line joined to the one after it, in
 * `quoteStartsAt` — without which every reply keeps two lines of Gmail's
 * plumbing in its preview, and "Got it, thanks." previews as "Got it, thanks. On
 * On Tue, 18 Aug 2026 at 12:55, Rebase <hello@rebase.p".
 */
const ATTRIBUTION_RE = /^(el|on)\s.{4,200}?\s(escribió|wrote|escreveu)\s*:?\s*$/i;
const QUOTE_MARKERS = [
    /^>/,
    /^-{2,}\s*$/,
    /^_{5,}\s*$/,
    /^-{3,}\s*(mensaje original|original message|forwarded message|mensaje reenviado)\s*-{3,}/i,
    ATTRIBUTION_RE,
    /^de\s*:\s*.+/i,
    /^from\s*:\s*.+/i,
    /^enviado (el|desde)\s*:/i,
    /^sent\s*:/i
];
/** The line index where quotation starts, or -1. The one primitive here. */
function quoteStartsAt(text) {
    const lines = (text ?? "").replace(/\r\n/g, "\n").split("\n");
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (QUOTE_MARKERS.some(re => re.test(line)))
            return i;
        // The wrapped attribution: this line and the next are one sentence.
        // Only tried when this line opens like one, so the join costs nothing
        // on the other 99% of lines.
        if (i + 1 < lines.length && /^(el|on)\s/i.test(line)
            && ATTRIBUTION_RE.test(`${line} ${lines[i + 1].trim()}`)) {
            return i;
        }
    }
    return -1;
}
/**
 * The part of a message its author actually wrote this time.
 *
 * A reply that quotes a thread of eight carries all eight in its body, so an
 * untrimmed preview in the inbox list is the *oldest* message every time — the
 * one thing nobody needs to see.
 */
export function stripQuotedReply(text) {
    const full = (text ?? "").replace(/\r\n/g, "\n");
    const at = quoteStartsAt(full);
    const kept = at === -1 ? full : full.split("\n").slice(0, at).join("\n");
    // A message that is *entirely* quotation — a bare forward with no comment —
    // would come back empty, and an empty preview is less useful than a quoted
    // one. Falling back to the whole text says "there is nothing new here"
    // honestly rather than by omission.
    return kept.trim() || full.trim();
}
/**
 * Whether there is genuinely quoted history worth offering to expand.
 *
 * The question the reader asks, and it is not answerable by comparing lengths: a
 * message can be shortened by the preview builder for reasons that have nothing
 * to do with quoting. `stripQuotedReply` also *falls back* to the whole text for
 * an all-quotation message, so "the stripped version is shorter" is false in
 * exactly the case where the quotation is everything.
 */
export function hasQuotedReply(text) {
    const at = quoteStartsAt(text);
    if (at === -1)
        return false;
    // All-quotation: `stripQuotedReply` returns the whole text, so there is
    // nothing the toggle could reveal that is not already on screen.
    return (text ?? "").replace(/\r\n/g, "\n").split("\n").slice(0, at).join("\n").trim().length > 0;
}
/** How much of a message the *list* shows. Two lines at a readable width. */
export const SNIPPET_LENGTH = 180;
/**
 * The preview line: unquoted, unwrapped, and cut on a word.
 *
 * For the inbox list only. It is emphatically not the message — see the note at
 * the top of this file about what happened when it was used as one.
 */
export function buildSnippet(text) {
    const flat = stripQuotedReply(text).replace(/\s+/g, " ").trim();
    if (flat.length <= SNIPPET_LENGTH)
        return flat;
    const cut = flat.slice(0, SNIPPET_LENGTH);
    const lastSpace = cut.lastIndexOf(" ");
    return `${(lastSpace > SNIPPET_LENGTH * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}
